import { useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useKnowledgeDocuments } from "../../hooks/useKnowledge";
import { KnowledgeStatusBadge } from "./KnowledgeStatusBadge";
import type { AssistantAttachment } from "../../types/assistant";

/**
 * "Choose from Documents" in the Innovate Assistant sidebar (task 1084),
 * patterned on `KnowledgeBaseDocumentPicker`.
 *
 * Nothing is uploaded: the User picks a document already in the account
 * library and it becomes the conversation's attachment. Only a Ready document
 * can ride a turn — the service refuses any other as a subject — so Ready rows
 * are selectable and every other row shows its status and stays disabled.
 *
 * A HeadlessUI `Dialog` so it portals out of the docked pane and sits above the
 * page rather than inside the pane's stacking context.
 */
interface SidebarDocumentPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (attachment: AssistantAttachment & { status: "ready" }) => void;
}

/** One page is enough to pick from; the search box narrows server-side. */
const LIBRARY_PAGE_SIZE = 100;

export function SidebarDocumentPicker({
  isOpen,
  onClose,
  onSelect,
}: SidebarDocumentPickerProps) {
  const [search, setSearch] = useState("");
  const { data, isLoading, error } = useKnowledgeDocuments(
    1,
    LIBRARY_PAGE_SIZE,
    {
      search: search || undefined,
    },
  );
  const documents = data?.data ?? [];

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center">
        <DialogPanel
          data-testid="sidebar-document-picker"
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <DialogTitle
              as="h3"
              className="text-lg font-medium text-gray-900 dark:text-white"
            >
              Choose from Documents
            </DialogTitle>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close dialog"
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-auto px-6 py-4">
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search your documents..."
                aria-label="Search your documents"
                data-testid="sidebar-document-picker-search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Loading documents...
                </div>
              </div>
            ) : error ? (
              <p className="text-center py-8 text-sm text-red-600 dark:text-red-400">
                Error loading documents. Please try again.
              </p>
            ) : documents.length === 0 ? (
              <p className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                {search
                  ? `No documents found matching "${search}"`
                  : "Your account has no documents yet."}
              </p>
            ) : (
              <div className="space-y-2">
                {documents.map((document) => {
                  const selectable = document.status === "ready";
                  return (
                    <button
                      key={document.id}
                      type="button"
                      disabled={!selectable}
                      onClick={() => {
                        setSearch("");
                        onSelect({
                          id: document.id,
                          name: document.name,
                          status: "ready",
                        });
                      }}
                      data-testid={`sidebar-picker-document-${document.id}`}
                      className="w-full text-left p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-700"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {document.name}
                        </span>
                        <KnowledgeStatusBadge
                          document={document}
                          showFailureReason={false}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default SidebarDocumentPicker;
